// client/src/v2/mentor-livro/travessia.tsx
// A TRAVESSIA (30/10/2026). A trilha do livro como um MAPA: o leitor é um
// viajante, as etapas do livro são regiões do terreno e a névoa cobre o que
// ele ainda não leu. Cada registro de páginas empurra a névoa: o caminho se
// desenha até o "Você está aqui" e as paradas novas acendem uma a uma.
//
//   - As paradas (o servidor decide o que já abriu: server/mentor-livro):
//     comentário (uma linha), insight (abre por cima), desafio (feito / não
//     feito, com sequência quando é diário), vídeo (o espaço da aula), marco
//     (fim de etapa) e fechamento (o fim do mapa).
//   - Sob a névoa, só a SILHUETA: a forma e a página ("algo te espera na p. 68").
//   - O texto do livro nunca passa por aqui: o mapa só tem o que o Mentor escreveu.
//   - AS VINHETAS (Munger, 31/10/2026): a etapa com `fundos` troca o terreno
//     desenhado por imagens FIXAS ao longo do caminho, como num mapa antigo —
//     na largura toda, o caminho passando pela lateral dissolvida, nada de
//     texto por cima e uma legenda curta embaixo (Vinheta, abaixo). Sem
//     `fundos`, o terreno. O caminho não alterna
//     certinho: fica do mesmo lado uma ou duas paradas (caminhada, abaixo).
//   - A 1ª ETAPA GRÁTIS (31/10/2026): sem o Pro, a 1ª etapa abre por inteiro e
//     as seguintes vêm trancadas (o nome e quantas paradas têm, sem silhueta).
//     Ao terminar a 1ª, ou ao tocar numa trancada, abre o convite do Pro.
//
// Desempenho (Android simples no Capacitor): só transform e opacity se animam;
// o parallax escreve direto no estilo, num requestAnimationFrame por rolagem;
// a névoa viva é do tamanho da tela (sticky). prefers-reduced-motion: sem
// abertura animada, sem parallax, sem partículas.
//
// Cores: só var(--tv-…) (travessia.css) e os tokens globais.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../services/apiV2';
import type { TrilhaItem, TrilhaAvaliacao, TravessiaMapa, TravessiaEtapa, TravessiaForma, TravessiaFundo } from '../../services/apiV2';
import type { AuthSession, BookV2 } from '../../types';
import { openExternalUrl, isNativeApp } from '../platform';
import { LojasDoApp, LINHA_SO_LOJAS } from '../baixar-app';
import { OPCOES, avisarTrilhaVista, blocos, inline } from './trilha';
import { separarPergunta, contextoDoInsight } from './gancho-caderno';
import type { ContextoDaNota } from '../nota-composer';
import { usePrecosPro, PRO_NOME } from '../nova/pro';
import { PlayerTelaCheia, capaDoVideo } from './video-aula';
import './travessia.css';

type Props = {
  book: BookV2;
  session: AuthSession | null;
  onBack: () => void;
  onRegistrar: () => void;
  onAbrirCaderno: (pagina?: number | null, contexto?: ContextoDaNota | null) => void;
  /** Interface nova: a chave Travessia | Caderno no lugar do título. */
  abas?: React.ReactNode;
  /** O pagamento das trilhas (o convite do Pro, na 1ª etapa grátis). */
  onPro?: () => void;
  /** Abre já com a folha deste insight/desafio (a Home: "Ontem você levou"). */
  abrirItem?: string;
};

/* ── o que a tela calcula do mapa ───────────────────────────────────────── */

/** x = onde fica o nó, em % da largura (o cartão sai dele para o lado de dentro). */
type Parada = { k: 'item'; item: TrilhaItem; lado: Lado; x: number } | { k: 'sombra'; forma: TravessiaForma; pagina: number; lado: Lado; x: number; proxima: boolean } | { k: 'aqui' }
  | { k: 'vinheta'; fundo: TravessiaFundo; n: number; lado: Beira };
type Lado = 'esq' | 'dir' | 'meio';
type Beira = 'esq' | 'dir';
type Trancada = { n: number; nome: string; paradas: number };
type Regiao = { etapa: TravessiaEtapa; paradas: Parada[]; aberta: boolean; trancada?: Trancada };

const ordemItem = (a: TrilhaItem, b: TrilhaItem) => a.pagina_gatilho - b.pagina_gatilho || a.ordem - b.ordem;

function etapaDaPagina(etapas: TravessiaEtapa[], p: number) {
  for (const e of etapas) if (p >= e.de && p <= e.ate) return e.n;
  if (!etapas.length) return 1;
  return p < etapas[0].de ? etapas[0].n : etapas[etapas.length - 1].n;
}

function montarRegioes(m: TravessiaMapa): Regiao[] {
  const etapas = [...m.livro.etapas].sort((a, b) => a.n - b.n);
  const itens = [...m.itens].sort(ordemItem);
  const adiante = [...m.adiante].sort((a, b) => a.pagina - b.pagina);
  const trancadas = new Map((m.trancadas || []).map((t) => [t.n, t]));
  let aquiEtapa = etapaDaPagina(etapas, Math.max(m.pagina, etapas[0]?.de ?? 0));
  // Leu além da 1ª etapa sem o Pro: o "aqui" fica no fim do que está aberto
  // (a névoa cobre as trancadas; a página certa continua no rótulo).
  if (trancadas.has(aquiEtapa)) aquiEtapa = [...etapas].reverse().find((e) => !trancadas.has(e.n))?.n ?? aquiEtapa;
  const andar = caminhada();
  let primeiraSombra = true;
  return etapas.map((e) => {
    const t = trancadas.get(e.n);
    const fundos = e.fundos || [];
    // trancada: só a 1ª imagem da etapa, de vitrine, acima do cartão do Pro
    if (t) return { etapa: e, paradas: fundos.length ? [{ k: 'vinheta', fundo: fundos[0], n: 0, lado: sorte(`v${e.n}`) < 0.5 ? 'esq' : 'dir' } as Parada] : [], aberta: false, trancada: t };
    // As paradas da etapa (abertas e por abrir) na ordem das páginas. A chave de
    // cada uma é a mesma antes e depois de abrir (página + forma), então o
    // desenho do mapa não muda quando a névoa recua.
    type Ponto = { forma: TravessiaForma; chave: string; vira: (lado: Lado, x: number) => Parada; aberta: boolean };
    const pontos: Ponto[] = [];
    const vistas = new Map<string, number>();
    const chaveDe = (pagina: number, forma: TravessiaForma) => { const c = `${pagina}.${forma}`; const k = vistas.get(c) || 0; vistas.set(c, k + 1); return `${c}.${k}`; };
    for (const it of itens) if (etapaDaPagina(etapas, it.pagina_gatilho) === e.n) {
      const forma = it.forma || 'comentario';
      pontos.push({ forma, chave: chaveDe(it.pagina_gatilho, forma), aberta: true, vira: (lado, x) => ({ k: 'item', item: it, lado, x }) });
    }
    for (const s of adiante) if (etapaDaPagina(etapas, s.pagina) === e.n) {
      const proxima = primeiraSombra; primeiraSombra = false;
      pontos.push({ forma: s.forma, chave: chaveDe(s.pagina, s.forma), aberta: false, vira: (lado, x) => ({ k: 'sombra', forma: s.forma, pagina: s.pagina, lado, x, proxima }) });
    }
    // As vinhetas: a 1ª logo abaixo do nome da etapa, as outras espalhadas
    // pelo caminho (no máximo uma a cada 3 paradas).
    const n = pontos.length;
    const k = fundos.length ? Math.min(fundos.length, Math.max(1, Math.ceil(n / 3))) : 0;
    const vaos = new Map<number, number>();
    for (let j = 0; j < k; j++) vaos.set(Math.round((j * n) / k), j);
    const paradas: Parada[] = [];
    let anterior: Lado | null = null;
    pontos.forEach((p, i) => {
      const meio = p.forma === 'marco' || p.forma === 'fechamento';
      const j = vaos.get(i);
      if (j !== undefined) {
        // A imagem fica de um lado; a parada de antes e a de depois, do outro
        // (o caminho contorna a imagem por ali).
        const passo = !meio && (anterior === 'esq' || anterior === 'dir') ? andar(p.chave, anterior) : meio ? null : andar(p.chave);
        const lado: Beira = passo ? (passo.lado === 'esq' ? 'dir' : 'esq') : sorte(`v${e.n}.${j}`) < 0.5 ? 'esq' : 'dir';
        paradas.push({ k: 'vinheta', fundo: fundos[j], n: j, lado });
        if (passo) { paradas.push(p.vira(passo.lado, passo.x)); anterior = passo.lado; return; }
      }
      if (meio) { paradas.push(p.vira('meio', 50)); anterior = 'meio'; return; }
      const passo = andar(p.chave);
      paradas.push(p.vira(passo.lado, passo.x)); anterior = passo.lado;
    });
    // o "você está aqui": depois da última parada aberta da etapa
    if (e.n === aquiEtapa) {
      let at = 0;
      paradas.forEach((p, i) => { if (p.k === 'item') at = i + 1; });
      paradas.splice(at, 0, { k: 'aqui' });
    }
    return { etapa: e, paradas, aberta: e.n <= aquiEtapa };
  });
}

/** Um número de 0 a 1 que sempre sai igual para a mesma chave. */
function sorte(chave: string) {
  let h = 2166136261;
  for (let i = 0; i < chave.length; i++) { h ^= chave.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 10007) / 10007;
}

/** O traçado do caminho, como num mapa: não alterna certinho. Fica do mesmo
 *  lado uma ou duas paradas (nunca três seguidas, a não ser para contornar uma
 *  imagem) e cada nó fica a uma distância diferente da beira (18–32%), então o
 *  caminho serpenteia em vez de fazer um zigue-zague regular. */
function caminhada() {
  let lado: Beira | null = null; let serie = 0; let beira = 25;
  return (chave: string, forcado?: Beira) => {
    const r = sorte(chave);
    const outro: Beira = lado === 'esq' ? 'dir' : 'esq';
    const l: Beira = forcado || (!lado ? (r < 0.5 ? 'esq' : 'dir') : serie >= 2 ? outro : r < 0.42 ? lado : outro);
    serie = l === lado ? serie + 1 : 1; lado = l;
    let d = 18 + Math.round(sorte(chave + 'x') * 14);
    if (serie > 1 && Math.abs(d - beira) < 7) d = beira < 25 ? beira + 8 : beira - 8;
    beira = d;
    return { lado: l, x: l === 'esq' ? d : 100 - d };
  };
}

/* ── o terreno: um ladrilho de 400×600 por tipo, repetido e espelhado ───── */

const F = (v: string, o = 1): React.CSSProperties => ({ fill: `var(--tv-${v})`, opacity: o });
const S = (v: string, w: number, o = 1): React.CSSProperties => ({ fill: 'none', stroke: `var(--tv-${v})`, strokeWidth: w, opacity: o, strokeLinecap: 'round' });

function Arvores({ x, y, n }: { x: number; y: number; n: number }) {
  const out: React.ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const ax = x + ((i * 23) % 64); const ay = y + ((i * 37) % 90); const h = 34 + ((i * 13) % 22);
    out.push(<path key={i} d={`M${ax} ${ay} l-${h * 0.36} ${h} h${h * 0.72} z`} style={F('arvore', 0.9)} />);
  }
  return <>{out}</>;
}

function Ladrilho({ terreno, i }: { terreno: string; i: number }) {
  let c: React.ReactNode;
  switch (terreno) {
    case 'bosque':
      c = <>
        <path d="M0 250 Q 110 200 210 240 T 400 225 V600 H0Z" style={F('relevo-longe', 0.55)} />
        <Arvores x={8} y={120} n={6} /><Arvores x={318} y={300} n={6} /><Arvores x={14} y={420} n={5} />
        <path d="M0 470 Q 140 420 260 460 T 400 450 V600 H0Z" style={F('relevo-perto', 0.5)} />
      </>; break;
    case 'rio':
      c = <>
        <path d="M0 160 Q 120 130 220 170 T 400 150 V600 H0Z" style={F('relevo-longe', 0.4)} />
        <path d="M-30 90 C 110 170, 290 120, 430 280 S 120 430, -30 560" style={S('agua', 54, 0.95)} />
        <path d="M-30 90 C 110 170, 290 120, 430 280 S 120 430, -30 560" style={{ ...S('agua-brilho', 2), strokeDasharray: '18 26' }} />
      </>; break;
    case 'serra':
      c = <>
        <path d="M0 250 L60 180 L110 220 L170 120 L240 210 L300 150 L360 220 L400 190 V600 H0Z" style={F('relevo-longe', 0.7)} />
        <path d="M60 180 L110 220 L170 120 L240 210 L300 150" style={S('relevo-luz', 2)} />
        <path d="M0 430 L80 360 L150 410 L230 330 L310 400 L400 350 V600 H0Z" style={F('relevo-perto', 0.6)} />
      </>; break;
    case 'vale':
      c = <>
        <path d="M0 100 C 120 240 150 420 175 600 H0Z" style={F('relevo-perto', 0.55)} />
        <path d="M400 140 C 290 260 250 430 225 600 H400Z" style={F('relevo-longe', 0.6)} />
        <path d="M200 0 C 185 200 215 400 200 600" style={S('agua', 6, 0.8)} />
      </>; break;
    case 'desfiladeiro':
      c = <>
        <path d="M0 0 H74 C 96 120 52 240 84 360 C 104 470 62 540 78 600 H0Z" style={F('rocha', 0.85)} />
        <path d="M400 0 H322 C 300 140 344 250 312 370 C 296 480 336 540 320 600 H400Z" style={F('rocha', 0.85)} />
        <path d="M0 140 H60 M0 300 H70 M0 470 H64 M400 210 H336 M400 380 H318 M400 520 H330" style={S('relevo-luz', 1.5)} />
      </>; break;
    case 'cume':
      c = <>
        <circle cx="60" cy="60" r="1.6" style={F('particula', 0.35)} /><circle cx="300" cy="40" r="1.2" style={F('particula', 0.3)} /><circle cx="350" cy="110" r="1.4" style={F('particula', 0.3)} />
        <path d="M0 600 L110 330 L170 380 L250 190 L310 300 L400 250 V600Z" style={F('relevo-longe', 0.7)} />
        <path d="M250 190 L226 244 L244 236 L258 254 L272 232 Z" style={F('relevo-luz', 1)} />
        <path d="M0 600 L90 470 L180 520 L280 430 L400 500 V600Z" style={F('relevo-perto', 0.6)} />
      </>; break;
    default: // campo
      c = <>
        <path d="M0 220 Q 90 170 180 210 T 400 200 V600 H0Z" style={F('relevo-longe', 0.55)} />
        <path d="M0 220 Q 90 170 180 210 T 400 200" style={S('relevo-luz', 2)} />
        <path d="M0 400 Q 120 340 230 390 T 400 370 V600 H0Z" style={F('relevo-perto', 0.5)} />
        <path d="M40 300 l3 -9 M48 302 l-2 -8 M330 470 l3 -9 M338 472 l-2 -8 M120 520 l3 -9" style={S('relevo-luz', 1.5)} />
      </>;
  }
  return (
    <div style={{ aspectRatio: '2 / 3', width: '100%', transform: i % 2 ? 'scaleX(-1)' : undefined }}>
      <svg viewBox="0 0 400 600" preserveAspectRatio="none" width="100%" height="100%" aria-hidden="true">{c}</svg>
    </div>
  );
}

/* ── as vinhetas do mapa (Munger, 31/10/2026) ───────────────────────────── */

/** Uma imagem fixa no caminho, como as ilustrações de um mapa antigo: na
 *  largura toda, deitada, com as bordas dissolvidas no papel (máscara, sem
 *  moldura) e sem texto por cima. O caminho passa por cima da lateral já
 *  dissolvida, pelos dois pontos de passagem (data-no) que ela leva. Embaixo,
 *  a legenda curta — quem é e por que está ali ("Título: explicação", o
 *  título em negrito). Carrega sob demanda (lazy). */
function Vinheta({ fundo, n, lado, id, vitrine }: { fundo: TravessiaFundo; n: number; lado: Beira; id: string; vitrine?: boolean }) {
  const dois = fundo.legenda ? fundo.legenda.indexOf(': ') : -1;
  return (
    <figure className={'tv-vinheta' + (vitrine ? ' tv-vinheta-vitrine' : '')} data-lado={lado} data-vinheta={n}>
      <img className="tv-vinheta-img" src={fundo.src} alt="" loading="lazy" decoding="async" draggable={false} data-fundo-img={n} style={{ objectPosition: fundo.foco }} />
      {!vitrine && fundo.legenda && (
        <figcaption className="tv-legenda" data-legenda={n}>
          {dois > 0 ? <><b>{fundo.legenda.slice(0, dois)}:</b> {fundo.legenda.slice(dois + 2)}</> : fundo.legenda}
        </figcaption>
      )}
      {!vitrine && <><span className="tv-ponto-no tv-passa" data-no={`${id}a`} aria-hidden="true" /><span className="tv-ponto-no tv-passa" data-no={`${id}b`} aria-hidden="true" /></>}
    </figure>
  );
}

/** A imagem da parada (02/11/2026, Os Safra): a foto, o mapa ou o recorte de
 *  notícia que o comentário, o insight ou o desafio traz. No cartão do mapa vai
 *  só a imagem (com `legenda`, também a legenda e o crédito); na folha, sempre
 *  com os dois. Carrega sob demanda (lazy). */
type ImagemDaParada = NonNullable<NonNullable<TrilhaItem['extra']>['imagem']>;
function FiguraParada({ imagem, legenda, folha }: { imagem: ImagemDaParada; legenda?: boolean; folha?: boolean }) {
  const dois = imagem.legenda ? imagem.legenda.indexOf(': ') : -1;
  return (
    <figure className={'tv-figura' + (folha ? ' tv-figura-folha' : '')} data-figura={imagem.src}>
      <img src={imagem.src} alt={imagem.legenda ? imagem.legenda.slice(0, dois > 0 ? dois : undefined) : ''} loading="lazy" decoding="async" draggable={false} />
      {legenda && (imagem.legenda || imagem.credito) && (
        <figcaption>
          {imagem.legenda && (dois > 0 ? <><b>{imagem.legenda.slice(0, dois)}:</b> {imagem.legenda.slice(dois + 2)}</> : imagem.legenda)}
          {imagem.credito && <span className="tv-figura-credito">{imagem.credito}</span>}
        </figcaption>
      )}
    </figure>
  );
}

/** O cartão do comentário: fechado, o cabeçalho e só a 1ª linha (reticências);
 *  aberto, o texto inteiro e as avaliações. A altura anima de leve (FLIP). */
/** O comentário com recorte de notícia (02/11/2026): fechado, uma linha sem as marcas
 *  do markdown; aberto, o recorte desenhado como no insight. */
const temRecorte = (corpo: string) => /^>\s*\*\*Recorte\b/m.test(corpo);
const linhaSemMarcas = (corpo: string) => corpo.replace(/^>\s?/gm, '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();

function CartaoComentario({ aberto, onAlternar, cab, texto, md, imagem, children }: { aberto: boolean; onAlternar: () => void; cab: React.ReactNode; texto: string; md?: string; imagem?: ImagemDaParada; children?: React.ReactNode }) {
  const el = useRef<HTMLDivElement>(null);
  const antes = useRef<number | null>(null);
  const alternar = () => { if (el.current) antes.current = el.current.offsetHeight; onAlternar(); };
  useLayoutEffect(() => {
    const c = el.current; const h0 = antes.current; antes.current = null;
    if (!c || h0 == null || menosMovimento()) return;
    const h1 = c.offsetHeight;
    if (Math.abs(h1 - h0) < 2) return;
    const fim = (e?: TransitionEvent) => {
      if (e && (e.target !== c || e.propertyName !== 'height')) return;
      c.style.height = ''; c.style.overflow = ''; c.style.transition = '';
      c.removeEventListener('transitionend', fim);
    };
    c.style.height = `${h0}px`; c.style.overflow = 'hidden';
    void c.offsetHeight;
    c.style.transition = 'height 220ms cubic-bezier(.2,.7,.2,1)';
    c.style.height = `${h1}px`;
    c.addEventListener('transitionend', fim);
    const t = setTimeout(() => fim(), 400);
    return () => clearTimeout(t);
  }, [aberto]);
  return (
    <div ref={el} className="tv-cartao tv-com" role="button" tabIndex={0} aria-expanded={aberto} data-comentario-aberto={aberto ? '' : undefined}
      onClick={alternar} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternar(); } }}>
      {cab}
      {imagem && <FiguraParada imagem={imagem} legenda={aberto} />}
      {aberto && md ? <MdTv md={md} /> : <p className="tv-linha">{texto}</p>}
      {children}
    </div>
  );
}

/* ── markdown nas cores da Travessia ────────────────────────────────────── */

function MdTv({ md }: { md: string }) {
  return (
    <div className="tv-md" data-trilha-md>
      {blocos(md).map((b, i) => {
        const k = 'b' + i;
        if (b.t === 'hr') return <hr key={k} />;
        if (b.t === 'h') return <h3 key={k}>{inline(b.linhas[0], k)}</h3>;
        if (b.t === 'hh') return <h4 key={k}>{inline(b.linhas[0], k)}</h4>;
        // o recorte de notícia (02/11/2026): "> **Recorte · Veículo, data**" e, nas linhas seguintes, o fato com as nossas palavras
        if (b.t === 'q' && /^\*\*Recorte\b/.test(b.linhas[0])) return (
          <blockquote key={k} className="tv-recorte" data-recorte>
            <div className="tv-mono tv-recorte-de">{b.linhas[0].replace(/\*\*/g, '')}</div>
            {b.linhas.slice(1).map((l, j) => <div key={j}>{inline(l, `${k}.${j}`)}</div>)}
          </blockquote>
        );
        if (b.t === 'q') return <blockquote key={k}>{b.linhas.map((l, j) => <div key={j}>{inline(l, `${k}.${j}`)}</div>)}</blockquote>;
        if (b.t === 'ul' || b.t === 'ol') { const L = b.t; return <L key={k}>{b.linhas.map((l, j) => <li key={j}>{inline(l, `${k}.${j}`)}</li>)}</L>; }
        return <p key={k}>{inline(b.linhas.join(' '), k)}</p>;
      })}
    </div>
  );
}

/* ── ícones (traço, na cor do texto) ────────────────────────────────────── */

const Ic = {
  voltar: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>,
  fechar: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>,
  comentario: <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5" /></svg>,
  insight: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.5 1 2.5h6c0-1 .2-1.7 1-2.5A6 6 0 0 0 12 3z" /></svg>,
  desafio: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l4 4L19 6" /></svg>,
  video: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>,
  marco: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 21V4M5 4h11l-2 4 2 4H5" /></svg>,
  cadeado: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>,
  fechamento: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20l6-10 4 6 3-4 5 8z" /><circle cx="17" cy="6" r="2" /></svg>,
};

const ROTULO: Record<TravessiaForma, string> = { comentario: 'Comentário', insight: 'Insight', desafio: 'Desafio', video: 'Vídeo-aula', marco: 'Marco', fechamento: 'Fim da travessia' };

/* ── a tela ─────────────────────────────────────────────────────────────── */

type Buraco = { x: number; y: number; w: number; h: number };
type Geo = { d: string; total: number; ate: number[]; x: number[]; y: number[]; nos: string[]; altura: number; buracos: Buraco[] };
type Fase = 'carregando' | 'antes' | 'abrindo' | 'pronto';

const chavePagina = (bookId: string) => `tv:pagina:${bookId}`;
const menosMovimento = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const chaveConvite = (bookId: string) => `tv:convite-pro:${bookId}`;

export function TravessiaScreen({ book, session, onBack, onRegistrar, onAbrirCaderno, abas, onPro, abrirItem }: Props) {
  const [mapa, setMapa] = useState<TravessiaMapa | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [fase, setFase] = useState<Fase>('carregando');
  const [geo, setGeo] = useState<Geo | null>(null);
  const [verFicha, setVerFicha] = useState(false);
  const [aberto, setAberto] = useState<TrilhaItem | null>(null);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [falhou, setFalhou] = useState<string | null>(null);
  const [convite, setConvite] = useState(false);
  const [aula, setAula] = useState<TrilhaItem | null>(null);
  // Cada carga do servidor é uma visita: avaliar ou marcar um desafio troca o
  // mapa no lugar, mas não recomeça a visita (nem rola de volta ao "aqui").
  const [visita, setVisita] = useState(0);
  // A abertura: o nó de onde a névoa sai, os itens que acendem, a duração.
  const abertura = useRef<{ deNo: number; novos: Set<string>; dur: number } | null>(null);
  const rolo = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<HTMLDivElement>(null);
  const terrenos = useRef<(HTMLDivElement | null)[]>([]);
  const reduz = useMemo(menosMovimento, []);

  /* carrega (e recarrega quando a pessoa registra páginas daqui mesmo) */
  useEffect(() => {
    let vivo = true;
    api.travessia(session, book.id).then((m) => {
      if (!vivo) return;
      const novos = m.itens.filter((i) => !i.visto_em);
      abertura.current = null;
      if (novos.length && !reduz) {
        const guardada = Number(localStorage.getItem(chavePagina(book.id)));
        const antes = Number.isFinite(guardada) && guardada > 0 && guardada < m.pagina ? guardada : Math.min(...novos.map((i) => i.pagina_gatilho)) - 1;
        const acende = new Set(m.itens.filter((i) => !i.visto_em && i.pagina_gatilho > antes).map((i) => i.id));
        if (acende.size) abertura.current = { deNo: -1, novos: acende, dur: Math.max(1800, Math.min(3600, 1300 + acende.size * 320)) };
      }
      setMapa(m); setErro(null); setGeo(null); setVisita((v) => v + 1);
      setFase(abertura.current ? 'antes' : 'pronto');
    }).catch((e: any) => {
      if (!vivo) return;
      setErro(e?.status === 404 ? 'Este livro ainda não tem a Travessia.' : 'Não deu para abrir o mapa agora. Tente de novo em instantes.');
    });
    return () => { vivo = false; };
  }, [session, book.id, book.read, reduz]);

  // Veio da Home para um insight/desafio: a folha dele abre uma vez, com o mapa carregado.
  const itemAbriu = useRef(false);
  useEffect(() => {
    if (!abrirItem || !mapa || itemAbriu.current) return;
    itemAbriu.current = true;
    const i = mapa.itens.find((x) => x.id === abrirItem && (x.forma === 'insight' || x.forma === 'desafio'));
    if (i) setAberto(i);
  }, [abrirItem, mapa]);

  const regioes = useMemo(() => (mapa ? montarRegioes(mapa) : []), [mapa]);
  const comFundos = regioes.some((r) => r.etapa.fundos?.length);
  const totalPaginas = mapa?.livro.total_paginas || book.pages || 0;

  /* mede os nós e traça o caminho (Catmull-Rom passando por eles) */
  const medir = useCallback(() => {
    const raiz = mapaRef.current;
    if (!raiz) return;
    const base = raiz.getBoundingClientRect();
    const els = Array.from(raiz.querySelectorAll<HTMLElement>('[data-no]'));
    if (els.length < 2) return;
    const x: number[] = []; const y: number[] = []; const nos: string[] = [];
    for (const el of els) {
      const r = el.getBoundingClientRect();
      x.push(r.left + r.width / 2 - base.left); y.push(r.top + r.height / 2 - base.top); nos.push(el.dataset.no || '');
    }
    let d = `M${x[0].toFixed(1)} ${y[0].toFixed(1)}`;
    for (let i = 0; i < x.length - 1; i++) {
      const i0 = Math.max(0, i - 1); const i3 = Math.min(x.length - 1, i + 2);
      const c1x = x[i] + (x[i + 1] - x[i0]) / 6; const c1y = y[i] + (y[i + 1] - y[i0]) / 6;
      const c2x = x[i + 1] - (x[i3] - x[i]) / 6; const c2y = y[i + 1] - (y[i3] - y[i]) / 6;
      d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${x[i + 1].toFixed(1)} ${y[i + 1].toFixed(1)}`;
    }
    // o comprimento do caminho até cada nó (um path de medida, fora da tela)
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg'); svg.setAttribute('style', 'position:absolute;width:0;height:0;visibility:hidden');
    const p = document.createElementNS(svgNS, 'path'); svg.appendChild(p); document.body.appendChild(svg);
    const ate: number[] = [0];
    const segs = d.split(' C');
    for (let i = 1; i < segs.length; i++) { p.setAttribute('d', segs.slice(0, i + 1).join(' C')); ate.push(p.getTotalLength()); }
    document.body.removeChild(svg);
    // O caminho passa POR BAIXO do nome da etapa: medimos o texto (não a caixa
    // inteira) de cada linha do cabeçalho e a máscara do trilho abre um vão ali.
    const buracos: Buraco[] = [];
    const faixa = document.createRange();
    for (const el of Array.from(raiz.querySelectorAll<HTMLElement>('[data-etapa-cab] > *'))) {
      faixa.selectNodeContents(el);
      for (const r of Array.from(faixa.getClientRects())) {
        if (r.width < 2) continue;
        buracos.push({ x: r.left - base.left - 12, y: r.top - base.top - 6, w: r.width + 24, h: r.height + 12 });
      }
    }
    setGeo({ d, total: ate[ate.length - 1], ate, x, y, nos, altura: raiz.scrollHeight, buracos });
  }, []);

  useLayoutEffect(() => {
    if (!mapa) return;
    medir();
    const raiz = mapaRef.current;
    if (!raiz || typeof ResizeObserver === 'undefined') return;
    let t: any; let largura = raiz.clientWidth;
    const ro = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => { if (raiz.clientWidth !== largura || fase === 'pronto') { largura = raiz.clientWidth; medir(); } }, 120); });
    ro.observe(raiz);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, [mapa, verFicha, expandido, medir, fase]);

  const iAqui = geo ? geo.nos.indexOf('aqui') : -1;
  const iDe = useMemo(() => {
    if (!geo || !abertura.current) return iAqui;
    const primeiro = geo.nos.findIndex((n) => abertura.current!.novos.has(n));
    return primeiro > 0 ? primeiro - 1 : 0;
  }, [geo, iAqui]);

  /* rola até o lugar e conduz a abertura */
  const rolarPara = (y: number) => {
    const r = rolo.current; if (!r) return;
    const alvo = Math.max(0, y - r.clientHeight * 0.55);
    r.scrollTop = alvo;
  };
  const jaRolou = useRef(false);
  // Os tempos da abertura vivem enquanto o mapa for o mesmo: a fase nova
  // remede o caminho (geo novo) e isso não pode cancelar a chegada ao "pronto".
  const tempos = useRef<ReturnType<typeof setTimeout>[]>([]);
  const camera = useRef(0);
  useEffect(() => {
    jaRolou.current = false;
    return () => { tempos.current.forEach(clearTimeout); tempos.current = []; cancelAnimationFrame(camera.current); };
  }, [visita]);
  // Na abertura a câmera acompanha o "Você está aqui" quadro a quadro (lendo a
  // posição que a transição dele está pintando): a pessoa vê a névoa recuar e
  // as paradas acenderem pelo caminho. Um toque na tela devolve o controle.
  const seguirMarca = (ate: number) => {
    const r = rolo.current; const marca = r?.querySelector<HTMLElement>('[data-aqui]');
    if (!r || !marca) return;
    let solto = false;
    const soltar = () => { solto = true; };
    r.addEventListener('touchstart', soltar, { once: true, passive: true });
    r.addEventListener('wheel', soltar, { once: true, passive: true });
    const passo = () => {
      if (solto) return;
      const y = new DOMMatrixReadOnly(getComputedStyle(marca).transform).m42;
      r.scrollTop = Math.max(0, y - r.clientHeight * 0.55);
      if (performance.now() < ate) camera.current = requestAnimationFrame(passo);
      else { r.removeEventListener('touchstart', soltar); r.removeEventListener('wheel', soltar); }
    };
    camera.current = requestAnimationFrame(passo);
  };
  useEffect(() => {
    if (!geo || !mapa || iAqui < 0 || jaRolou.current) return;
    jaRolou.current = true;
    if (fase === 'antes' && abertura.current) {
      rolarPara(geo.y[iDe]);
      const dur = abertura.current.dur;
      tempos.current.push(
        setTimeout(() => { setFase('abrindo'); seguirMarca(performance.now() + dur + 250); }, 650),
        setTimeout(() => { setFase('pronto'); concluirVisita(); }, 650 + dur + 300),
      );
      return;
    }
    rolarPara(mapa.pagina > 0 ? geo.y[iAqui] : 0);
    concluirVisita();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, mapa]);

  // Marca vistas as paradas abertas (o selo NOVA some na próxima visita) e
  // guarda até onde a névoa já recuou neste aparelho.
  const concluirVisita = () => {
    if (!mapa) return;
    try { localStorage.setItem(chavePagina(book.id), String(mapa.pagina)); } catch { /* sem armazenamento: só não anima de onde parou */ }
    if (mapa.itens.some((i) => !i.visto_em)) api.marcarTrilhaVista(session, book.id).then(() => avisarTrilhaVista(book.id)).catch(() => {});
  };

  // Terminou a 1ª etapa sem o Pro: o convite abre sozinho uma vez (neste
  // aparelho), mas só quando a pessoa chega às etapas trancadas — o 1º cartão
  // trancado sobe até a metade de cima do rolo, acima do "você está aqui".
  // Antes disso ela ainda está vendo o que acabou de abrir; com a folha de um
  // insight (ou a ficha) aberta, espera fechar. Depois, fica no toque das trancadas.
  const [naTrancada, setNaTrancada] = useState(false);
  const conviteAbriu = useRef(false);
  useEffect(() => {
    setNaTrancada(false);
    if (fase !== 'pronto' || !mapa || mapa.acesso !== 'etapa1' || !mapa.etapa1_concluida || !mapa.trancadas?.length) return;
    const r = rolo.current;
    const alvo = r?.querySelector('[data-abrir-convite]');
    if (!r || !alvo || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(([e]) => setNaTrancada(e.isIntersecting), { root: r, rootMargin: '0px 0px -45% 0px' });
    obs.observe(alvo);
    return () => obs.disconnect();
  }, [fase, mapa]);
  useEffect(() => {
    if (!naTrancada || aberto || verFicha || convite || conviteAbriu.current) return;
    let visto = false;
    try { visto = !!localStorage.getItem(chaveConvite(book.id)); } catch { /* sem armazenamento: abre */ }
    conviteAbriu.current = true;
    if (visto) return;
    setConvite(true);
    try { localStorage.setItem(chaveConvite(book.id), '1'); } catch { /* idem */ }
  }, [naTrancada, aberto, verFicha, convite, book.id]);

  /* parallax do terreno */
  useEffect(() => {
    if (reduz) return;
    const r = rolo.current; if (!r) return;
    let pedido = 0;
    const mover = () => {
      pedido = 0;
      const meio = r.clientHeight / 2;
      const topo = r.getBoundingClientRect().top;
      for (const el of terrenos.current) {
        if (!el) continue;
        const s = el.parentElement!.getBoundingClientRect();
        if (s.bottom < topo - 200 || s.top > topo + r.clientHeight + 200) continue;
        const dy = (s.top - topo + s.height / 2 - meio) * -0.09;
        el.style.transform = `translate3d(0, ${dy.toFixed(1)}px, 0)`;
      }
    };
    const rolar = () => { if (!pedido) pedido = requestAnimationFrame(mover); };
    r.addEventListener('scroll', rolar, { passive: true });
    mover();
    return () => { r.removeEventListener('scroll', rolar); if (pedido) cancelAnimationFrame(pedido); };
  }, [reduz, regioes]);

  /* ações nas paradas */
  const trocar = (id: string, f: (i: TrilhaItem) => TrilhaItem) => {
    setMapa((m) => (m ? { ...m, itens: m.itens.map((i) => (i.id === id ? f(i) : i)) } : m));
    setAberto((a) => (a && a.id === id ? f(a) : a));
  };
  const avaliar = async (item: TrilhaItem, v: TrilhaAvaliacao | null) => {
    const antes = item.avaliacao;
    trocar(item.id, (i) => ({ ...i, avaliacao: v }));
    try { await api.avaliarTrilha(session, item.id, v); setFalhou(null); } catch { trocar(item.id, (i) => ({ ...i, avaliacao: antes })); setFalhou(item.id); }
  };
  const marcar = async (item: TrilhaItem, v: boolean | null) => {
    const antes = item.desafio;
    const base = antes || { hoje: null, ultimo: null, ultimo_dia: null, feitos: 0, sequencia: 0 };
    const otimista = { ...base, hoje: v, ultimo: v, feitos: Math.max(0, base.feitos + (v === true ? 1 : 0) - (base.hoje === true ? 1 : 0)), sequencia: v === true ? Math.max(1, base.sequencia + (base.hoje === true ? 0 : 1)) : v === false ? 0 : base.hoje === true ? Math.max(0, base.sequencia - 1) : base.sequencia };
    trocar(item.id, (i) => ({ ...i, desafio: otimista }));
    try {
      const r = await api.marcarDesafio(session, item.id, v);
      trocar(item.id, (i) => ({ ...i, desafio: r.estado }));
      setFalhou(null);
    } catch { trocar(item.id, (i) => ({ ...i, desafio: antes })); setFalhou(item.id); }
  };

  /* números do topo */
  const pagina = mapa?.pagina || 0;
  const pct = totalPaginas ? Math.min(100, Math.round((pagina / totalPaginas) * 100)) : 0;
  const etapasFeitas = mapa ? mapa.livro.etapas.filter((e) => pagina >= e.ate).length : 0;
  const desafios = mapa ? [...mapa.itens.filter((i) => i.forma === 'desafio'), ...mapa.adiante.filter((a) => a.forma === 'desafio')] : [];
  const desafiosFeitos = mapa ? mapa.itens.filter((i) => i.forma === 'desafio' && (i.desafio?.feitos || 0) > 0).length : 0;
  const proxima = mapa?.adiante.length ? Math.min(...mapa.adiante.map((a) => a.pagina)) : null;
  const antesDeLer = !!mapa && (pagina === 0 || mapa.itens.length === 0);
  const nome = mapa?.livro.nome || 'A Travessia';

  /* posição da névoa e do "você está aqui" */
  const abrindo = fase === 'abrindo';
  const iMarca = fase === 'antes' ? iDe : iAqui;
  const marcaY = geo && iMarca >= 0 ? geo.y[iMarca] : 0;
  const marcaX = geo && iAqui >= 0 ? geo.x[iAqui] : 0;
  const nevoaY = marcaY + 170;
  const litLen = geo && iMarca >= 0 ? geo.ate[iMarca] : 0;
  const dur = abertura.current?.dur || 2600;
  const atrasoDe = (id: string) => {
    if (!geo || iAqui < 0) return 0;
    const k = geo.nos.indexOf(id); if (k < 0) return 0;
    const a = geo.y[iDe]; const b = geo.y[iAqui];
    return Math.round(Math.max(0, Math.min(1, (geo.y[k] - a) / Math.max(1, b - a))) * dur * 0.85);
  };
  const estadoDe = (i: TrilhaItem) => {
    if (i.forma === 'desafio') return (i.extra?.diario ? i.desafio?.hoje === true : i.desafio?.ultimo === true) ? 'concluida' : (!i.visto_em ? 'nova' : 'vista');
    if (i.avaliacao) return 'concluida';
    return !i.visto_em ? 'nova' : 'vista';
  };

  /* ── desenho ──────────────────────────────────────────────────────────── */

  /** Feito / Não feito de um desafio (e a sequência, se é diário): no cartão e na folha. */
  const marcacao = (i: TrilhaItem) => {
    const st = i.desafio;
    const valor = i.extra?.diario ? st?.hoje ?? null : st?.ultimo ?? null;
    return (
      <>
        <div className="tv-desafio-botoes" role="group" aria-label={i.extra?.diario ? 'Hoje' : 'Desafio'}>
          <button type="button" data-v="feito" aria-pressed={valor === true} onClick={() => marcar(i, valor === true ? null : true)}>{valor === true ? 'Feito ✓' : 'Feito'}</button>
          <button type="button" data-v="nao" aria-pressed={valor === false} onClick={() => marcar(i, valor === false ? null : false)}>Não feito</button>
        </div>
        {i.extra?.diario && (
          <div className="tv-sequencia" data-sequencia={st?.sequencia || 0}>
            <b>{st?.sequencia || 0}</b>
            <span>{(st?.sequencia || 0) === 1 ? 'dia seguido' : 'dias seguidos'} · desafio diário{st?.feitos ? ` · ${st.feitos} no total` : ''}</span>
          </div>
        )}
      </>
    );
  };

  const cartao = (i: TrilhaItem) => {
    const forma = i.forma || 'comentario';
    const nova = estadoDe(i) === 'nova';
    const selo = nova ? <span className="tv-selo-nova tv-mono">nova</span> : null;
    const cab = <div className="tv-mono">{ROTULO[forma]} · p. {i.pagina_gatilho}{selo}</div>;
    const chips = (
      <div className="tv-chips" role="group" aria-label="Como esta parada chegou para você?">
        {OPCOES.map((o) => <button key={o.v} type="button" className="tv-chip" aria-pressed={i.avaliacao === o.v} data-avaliacao={o.v} onClick={(e) => { e.stopPropagation(); avaliar(i, i.avaliacao === o.v ? null : o.v); }}>{o.rotulo}</button>)}
      </div>
    );
    const aviso = falhou === i.id ? <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>Não salvou. Tente de novo.</p> : null;
    if (forma === 'comentario') {
      const ab = expandido === i.id;
      return (
        <CartaoComentario aberto={ab} onAlternar={() => setExpandido(ab ? null : i.id)} cab={cab} texto={linhaSemMarcas(i.corpo)} md={temRecorte(i.corpo) ? i.corpo : undefined} imagem={i.extra?.imagem}>
          {ab && chips}{aviso}
        </CartaoComentario>
      );
    }
    if (forma === 'insight') {
      const essencial = (i.corpo.match(/\*\*O essencial\.?\*\*\s*([^\n]+)/i) || [])[1];
      return (
        <button type="button" className="tv-cartao" onClick={() => setAberto(i)} data-abrir-insight={i.id}>
          {cab}
          {i.extra?.imagem && <FiguraParada imagem={i.extra.imagem} />}
          <h3>{i.titulo}</h3>
          {essencial && <p>{essencial.replace(/\*\*|__/g, '').slice(0, 150)}{essencial.length > 150 ? '…' : ''}</p>}
          <p style={{ color: 'var(--tv-caminho)', fontWeight: 600, fontSize: 13 }}>Ler o insight →</p>
        </button>
      );
    }
    if (forma === 'desafio') {
      return (
        <div className="tv-cartao" data-desafio={i.id}>
          {cab}
          {i.extra?.imagem && <FiguraParada imagem={i.extra.imagem} legenda />}
          <h3>{i.titulo}</h3>
          <MdTvCurto md={i.corpo} />
          {marcacao(i)}
          {aviso}
        </div>
      );
    }
    if (forma === 'video') {
      const url = i.extra?.url;
      return (
        <div className="tv-cartao" data-video={i.id}>
          {cab}
          <h3>{i.titulo}</h3>
          <div className="tv-video" role={url ? 'button' : undefined} aria-label={url ? `Assistir: ${i.titulo}` : undefined}
            onClick={url ? () => setAula(i) : undefined} style={url ? { cursor: 'pointer' } : undefined} data-video-tocar={url ? i.id : undefined}>
            {url && capaDoVideo(url) && <img className="tv-video-capa" src={capaDoVideo(url)!} alt="" loading="lazy" />}
            <div className="tv-video-luz" />
            <div className="tv-video-play">{Ic.video}</div>
            {i.extra?.duracao && <span className="tv-video-dur">{i.extra.duracao}</span>}
          </div>
          <p>{i.corpo.replace(/\s+/g, ' ').trim()}</p>
          <div className="tv-mono" style={{ marginTop: 8, color: 'var(--ink-3)' }} data-video-estado>{url ? `${i.extra?.tipo || 'aula'} · toque para assistir` : 'Vídeo em produção · disponível em breve'}</div>
        </div>
      );
    }
    if (forma === 'marco') {
      const e = mapa!.livro.etapas.find((x) => x.n === etapaDaPagina(mapa!.livro.etapas, i.pagina_gatilho));
      const daEtapa = mapa!.itens.filter((x) => e && x.pagina_gatilho >= e.de && x.pagina_gatilho <= e.ate);
      const ds = daEtapa.filter((x) => x.forma === 'desafio');
      return (
        <div className="tv-cartao" data-marco={i.id} style={{ textAlign: 'center' }}>
          <div className="tv-mono">Etapa {e?.n} concluída{selo}</div>
          <h3>{i.titulo}</h3>
          <MdTvCurto md={i.corpo} />
          <div className="tv-contas" style={{ justifyContent: 'center' }}>
            <span><b>{daEtapa.length}</b>paradas</span>
            <span><b>{ds.filter((x) => (x.desafio?.feitos || 0) > 0).length}/{ds.length}</b>desafios</span>
          </div>
        </div>
      );
    }
    // fechamento
    return (
      <div className="tv-cartao" data-fechamento={i.id}>
        <div className="tv-mono" style={{ textAlign: 'center' }}>{ROTULO.fechamento}{selo}</div>
        <h3 style={{ textAlign: 'center', fontSize: 26 }}>{i.titulo}</h3>
        <div style={{ marginTop: 10 }}><MdTv md={i.corpo} /></div>
        {/* 02/11/2026: as trilhas ficaram sem vídeo; o quadro do vídeo final só aparece se o fechamento ainda trouxer um */}
        {(i.extra?.url || i.extra?.duracao) && (
          <div className="tv-video" style={{ margin: '4px 0 0' }}>
            <div className="tv-video-luz" /><div className="tv-video-play">{Ic.video}</div>
            {!i.extra?.url && <span className="tv-video-estado">Em produção</span>}
            {i.extra?.duracao && <span className="tv-video-dur">{i.extra.duracao}</span>}
          </div>
        )}
        <div className="tv-contas" style={{ justifyContent: 'center' }}>
          <span><b>{mapa!.itens.length}</b>paradas</span>
          <span><b>{desafiosFeitos}/{desafios.length}</b>desafios</span>
        </div>
        <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
          <button type="button" className="tv-botao" onClick={() => onAbrirCaderno(null)}>Abrir o caderno de aplicação</button>
        </div>
        {chips}
      </div>
    );
  };

  const no = (forma: TravessiaForma) => (Ic as any)[forma] || Ic.comentario;

  let cont = 0;
  const parada = (p: Parada, key: string, trancada: boolean) => {
    if (p.k === 'vinheta') return <Vinheta key={key} fundo={p.fundo} n={p.n} lado={p.lado} id={`v${key}`} vitrine={trancada} />;
    if (p.k === 'aqui') {
      return (
        <div key={key} className="tv-lugar-aqui">
          <span className="tv-ponto-no" data-no="aqui" />
        </div>
      );
    }
    if (p.k === 'sombra') {
      return (
        <div key={key} className="tv-sombra" data-lado={p.lado} data-forma={p.forma} data-sombra={p.pagina} style={{ ['--tv-x' as any]: `${p.x}%` }}>
          <span className="tv-no" data-no={`s${cont++}`}>{no(p.forma)}</span>
          <div className="tv-sombra-forma" />
          {p.proxima && <div className="tv-sombra-rotulo"><span>algo te espera na p. {p.pagina}</span></div>}
        </div>
      );
    }
    const i = p.item; const forma = i.forma || 'comentario';
    const acende = abertura.current?.novos.has(i.id);
    const cls = acende ? (fase === 'antes' ? ' tv-oculta' : fase === 'abrindo' ? ' tv-acende' : '') : '';
    return (
      <div key={key} className={'tv-parada' + cls} data-lado={p.lado} data-forma={forma} data-estado={estadoDe(i)} data-item={i.id}
        style={{ ['--tv-atraso' as any]: `${atrasoDe(i.id)}ms`, ['--tv-x' as any]: `${p.x}%` }}>
        <span className="tv-no" data-no={i.id}>{no(forma)}</span>
        {cartao(i)}
      </div>
    );
  };

  const ficha = mapa?.livro.ficha;
  const etapasOrd = mapa ? [...mapa.livro.etapas].sort((a, b) => a.n - b.n) : [];
  const mostraFicha = !!ficha && (antesDeLer || verFicha);

  return (
    <div className="tv-tela" data-travessia={book.id} data-fase={fase} data-fundos={comFundos ? '' : undefined} style={{ ['--tv-abrir' as any]: `${dur}ms` }}>
      <div className="tv-topo">
        <button type="button" className="tv-botao-redondo" onClick={onBack} aria-label="Voltar">{Ic.voltar}</button>
        {abas ? (
          <div className="tv-topo-titulo" style={{ display: 'flex', justifyContent: 'center', paddingRight: 46 }}>{abas}</div>
        ) : (
          <div className="tv-topo-titulo">
            <div className="tv-mono" style={{ color: 'var(--tv-caminho)' }}>{nome}</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 18, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mapa?.livro.titulo || book.title}</div>
          </div>
        )}
      </div>
      {mapa && (
        <div className="tv-progresso" data-progresso>
          <div className="tv-progresso-linha tv-mono">
            <span><b>{pct}%</b> do livro</span><i>·</i>
            <span><b>{etapasFeitas}/{mapa.livro.etapas.length}</b> etapas</span><i>·</i>
            <span><b>{desafiosFeitos}/{desafios.length}</b> desafios</span>
          </div>
          <div className="tv-progresso-filete" aria-hidden="true"><span style={{ transform: `scaleX(${pct / 100})` }} /></div>
        </div>
      )}

      <div className="tv-grao" />
      <div className="tv-rolo om-scroll" ref={rolo}>
        {erro && (
          <div className="tv-inicio" style={{ paddingTop: 80 }}>
            <h1 style={{ fontSize: 28 }}>{erro}</h1>
            <p style={{ marginTop: 18 }}><button type="button" className="tv-botao tv-sec" onClick={onBack}>Voltar</button></p>
          </div>
        )}
        {!mapa && !erro && <div className="tv-carregando"><div><div className="tv-mono">{nome}</div><p style={{ marginTop: 8 }}>Abrindo o mapa…</p></div></div>}
        {mapa && (
          <div className="tv-mapa" ref={mapaRef}>
            <div className="tv-inicio" data-inicio>
              {comFundos && (
                <svg className="tv-rosa" viewBox="-30 -30 60 60" aria-hidden="true" data-rosa>
                  <circle r="21" fill="none" stroke="currentColor" strokeWidth="0.8" />
                  <circle r="24" fill="none" stroke="currentColor" strokeWidth="0.4" strokeDasharray="1 2.2" />
                  <path d="M0 -28 L3.2 -3.2 L0 0 L-3.2 -3.2Z M0 28 L3.2 3.2 L0 0 L-3.2 3.2Z M-28 0 L-3.2 3.2 L0 0 L-3.2 -3.2Z M28 0 L3.2 3.2 L0 0 L3.2 -3.2Z" fill="currentColor" fillOpacity="0.85" />
                  <path d="M0 0 L12 -12 M0 0 L-12 -12 M0 0 L12 12 M0 0 L-12 12" stroke="currentColor" strokeWidth="0.8" />
                </svg>
              )}
              <div className="tv-mono" style={{ color: 'var(--tv-caminho)' }}>{mapa.livro.autor}</div>
              <h1>Aqui começa a sua jornada</h1>
              <p>{antesDeLer ? 'Comece a ler para abrir o caminho.' : `Você está na p. ${pagina} de ${totalPaginas}.${proxima ? ` A próxima parada fica na p. ${proxima}.` : ''}`}</p>
              {!antesDeLer && ficha && (
                <p style={{ marginTop: 12 }}><button type="button" className="tv-chip" aria-expanded={verFicha} onClick={() => setVerFicha((v) => !v)}>{verFicha ? 'Fechar a ficha do livro' : 'Ver a ficha do livro'}</button></p>
              )}
            </div>

            {mostraFicha && ficha && (
              <div className="tv-ficha" data-ficha>
                {/* Quem já decidiu ler não precisa rolar ~3 telas até o botão. */}
                {antesDeLer && (
                  <button type="button" className="tv-pular" data-pular-ficha onClick={() => {
                    const alvo = rolo.current?.querySelector<HTMLElement>('[data-comecar]');
                    if (!alvo) return;
                    alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    alvo.focus({ preventScroll: true });
                  }}>Pular para começar a ler ↓</button>
                )}
                <p className="tv-gancho">{ficha.gancho}</p>
                <h3>O que você leva desta travessia</h3>
                <ul>{ficha.resultados.map((r, k) => <li key={k}>{r}</li>)}</ul>
                <h3>Para quem é</h3>
                <ul>{ficha.para_quem.map((r, k) => <li key={k}>{r}</li>)}</ul>
                <h3>Para quem não é</h3>
                <ul>{ficha.nao_para_quem.map((r, k) => <li key={k}>{r}</li>)}</ul>
                <h3>O que tem no caminho</h3>
                <ul>{ficha.na_trilha.map((r, k) => <li key={k}>{r}</li>)}</ul>
                <h3>Um desafio do caminho</h3>
                <div className="tv-cartao" style={{ boxShadow: 'none', maxWidth: 'none' }}>
                  <div className="tv-mono">Desafio</div>
                  <h3 style={{ margin: '4px 0 0', fontFamily: 'var(--font-serif)', fontSize: 20, letterSpacing: 0, textTransform: 'none', color: 'var(--ink)' }}>{ficha.desafio_exemplo.titulo}</h3>
                  <p>{ficha.desafio_exemplo.corpo}</p>
                </div>
                <h3>As etapas</h3>
                <div className="tv-ficha-etapas">{etapasOrd.map((e) => <div key={e.n}><span>p. {e.de}–{e.ate}</span><span><b style={{ fontWeight: 600, color: 'var(--ink)' }}>{e.nome}.</b> {e.resumo}</span></div>)}</div>
                <h3>Tempo e esforço</h3>
                <ul><li>{ficha.tempo}</li><li>{ficha.esforco}</li></ul>
                <div className="tv-ficha-botoes">
                  <button type="button" className="tv-botao tv-sec" onClick={() => openExternalUrl(ficha.amazon)} data-amazon>Comprar na Amazon</button>
                  <button type="button" className="tv-botao tv-sec" disabled aria-disabled="true">Na sua estante ✓</button>
                  <button type="button" className="tv-botao" onClick={onRegistrar} data-comecar>{antesDeLer ? 'Começar a travessia' : 'Registrar páginas'}</button>
                </div>
                {ficha.depois?.length > 0 && (<>
                  <h3>Depois deste, leia</h3>
                  <ul>{ficha.depois.map((l, k) => <li key={k}><b style={{ color: 'var(--ink)', fontWeight: 600 }}>{l.titulo}</b>, {l.autor}. {l.porque}</li>)}</ul>
                </>)}
                {ficha.creditos_imagens && <p className="tv-creditos" data-creditos>{ficha.creditos_imagens}</p>}
              </div>
            )}

            <div className="tv-inicio-no"><span className="tv-ponto-no" data-no="inicio" /></div>

            {regioes.map((r, ri) => {
              const n = r.paradas.length;
              const ladrilhos = Math.max(2, Math.ceil((260 + n * 150) / 560) + 1);
              // (o servidor já limita a 4; a lista é a mesma referência enquanto o mapa for o mesmo)
              const fundos = r.etapa.fundos?.length ? r.etapa.fundos : null;
              return (
                <section key={r.etapa.n} className="tv-etapa" data-etapa={r.etapa.n} data-terreno={r.etapa.terreno} data-etapa-trancada={r.trancada ? r.etapa.n : undefined} data-fundos={fundos ? fundos.length : undefined}>
                  {!fundos && (
                    <div className="tv-terreno" ref={(el) => { terrenos.current[ri] = el; }} style={{ top: -90, bottom: -90 }}>
                      {Array.from({ length: ladrilhos }, (_, k) => <Ladrilho key={k} terreno={r.etapa.terreno} i={k + ri} />)}
                    </div>
                  )}
                  <div className={'tv-etapa-cab' + (r.aberta ? '' : ' tv-sob')} data-etapa-cab>
                    <div className="tv-mono" style={{ color: r.aberta ? 'var(--tv-caminho)' : undefined }}>Etapa {r.etapa.n} · p. {r.etapa.de}–{r.etapa.ate}</div>
                    <h2>{r.etapa.nome}</h2>
                    {r.aberta && <p>{r.etapa.resumo}</p>}
                  </div>
                  {r.paradas.map((p, k) => parada(p, `${r.etapa.n}.${k}`, !!r.trancada))}
                  {r.trancada && (
                    <div className="tv-trancada">
                      <span className="tv-no tv-no-trancado" data-no={`t${r.etapa.n}`}>{Ic.cadeado}</span>
                      <button type="button" className="tv-cartao tv-trancada-cartao" onClick={() => setConvite(true)} data-abrir-convite={r.etapa.n}>
                        <div className="tv-mono">Etapa {r.etapa.n} · trancada</div>
                        <h3 data-paradas-trancadas>{r.trancada.paradas} {r.trancada.paradas === 1 ? 'parada' : 'paradas'}</h3>
                        <p>Abre com o {PRO_NOME}.</p>
                      </button>
                    </div>
                  )}
                </section>
              );
            })}

            {geo && (
              <svg className="tv-trilho" width="100%" height={geo.altura} aria-hidden="true" style={{ height: geo.altura }}>
                <defs>
                  <mask id={`tv-vaos-${book.id}`} maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height={geo.altura}>
                    <rect x="0" y="0" width="100%" height={geo.altura} fill="#fff" />
                    {geo.buracos.map((b, k) => <rect key={k} x={b.x} y={b.y} width={b.w} height={b.h} rx={b.h / 2} fill="#000" />)}
                  </mask>
                </defs>
                <g mask={`url(#tv-vaos-${book.id})`} data-trilho-vaos={geo.buracos.length}>
                  <path className="tv-base" d={geo.d} />
                  <path className={'tv-aceso-brilho' + (abrindo ? ' tv-desenha' : '')} d={geo.d} style={{ strokeDasharray: `${geo.total} ${geo.total + 10}`, strokeDashoffset: geo.total - litLen }} />
                  <path className={'tv-aceso' + (abrindo ? ' tv-desenha' : '')} d={geo.d} style={{ strokeDasharray: `${geo.total} ${geo.total + 10}`, strokeDashoffset: geo.total - litLen }} />
                </g>
              </svg>
            )}

            {geo && iAqui >= 0 && (<>
              <div className={'tv-nevoa' + (abrindo ? ' tv-abrindo' : '')} style={{ transform: `translate3d(0, ${nevoaY}px, 0)` }} data-nevoa>
                <div className="tv-nevoa-borda" />
                <div className="tv-nevoa-corpo">
                  {!reduz && <div className="tv-nevoa-viva"><div className="tv-nuvem n1" /><div className="tv-nuvem n2" /><div className="tv-nuvem n3" /></div>}
                </div>
              </div>
              <div className={'tv-aqui' + (abrindo ? ' tv-abrindo' : '')} style={{ transform: `translate3d(${marcaX}px, ${marcaY}px, 0)` }} data-aqui>
                <div className="tv-aqui-halo" />
                {!reduz && [0, 1, 2, 3, 4, 5, 6].map((k) => <span key={k} className="tv-particula" style={{ left: -40 + ((k * 29) % 80), top: -10 + ((k * 17) % 30), animationDelay: `${(k * 1.3).toFixed(1)}s`, animationDuration: `${7 + (k % 3) * 2}s` }} />)}
                <div className="tv-aqui-ponto" />
                <div className="tv-aqui-rotulo">
                  <b>{antesDeLer ? 'O começo do caminho' : 'Você está aqui'}</b>
                  <small>{antesDeLer ? 'registre as primeiras páginas' : `p. ${pagina}${proxima ? ` · próxima parada na p. ${proxima}` : ''}`}</small>
                </div>
              </div>
            </>)}
          </div>
        )}
      </div>

      {/* Com o convite aberto a barra sai: nada dela fica sob a folha. */}
      {mapa && !mostraFicha && !convite && (
        <div className="tv-rodape">
          <button type="button" className="tv-botao" onClick={onRegistrar} data-registrar>Registrar páginas e abrir o caminho</button>
        </div>
      )}

      {convite && mapa?.trancadas && (
        <ConvitePro restantes={mapa.trancadas.length} terminou={!!mapa.etapa1_concluida} onFechar={() => setConvite(false)}
          onAssinar={onPro ? () => { setConvite(false); onPro(); } : undefined} />
      )}

      {aula?.extra?.url && <PlayerTelaCheia url={aula.extra.url} titulo={aula.titulo || 'Vídeo-aula'} onFechar={() => setAula(null)} />}

      {aberto && (
        <FolhaInsight item={aberto} falhou={falhou === aberto.id} onFechar={() => setAberto(null)} onAvaliar={(v) => avaliar(aberto, v)}
          onCaderno={(c) => { const p = aberto.pagina_gatilho; setAberto(null); onAbrirCaderno(p, c); }}
          desafio={aberto.forma === 'desafio' ? marcacao(aberto) : undefined} />
      )}
    </div>
  );
}

/** O convite do Pro na 1ª etapa grátis. O preço é o do plano único
 *  (plans.ts, ou o da loja no app nativo) — nunca escrito aqui.
 *  No SITE o Pro não se vende (só lojas, 31/10/2026): no lugar do "Assinar"
 *  vêm as duas lojas, para assinar pelo app com a mesma conta. */
function ConvitePro({ restantes, terminou, onFechar, onAssinar }: { restantes: number; terminou: boolean; onFechar: () => void; onAssinar?: () => void }) {
  const { ano } = usePrecosPro();
  const outras = restantes === 1 ? 'A outra' : `As outras ${restantes}`;
  return (
    <div className="tv-convite-fundo" onClick={onFechar} data-convite-pro>
      <div className="tv-convite" role="dialog" aria-modal="true" aria-label={PRO_NOME} onClick={(e) => e.stopPropagation()}>
        <div className="tv-mono" style={{ color: 'var(--tv-caminho)' }}>{PRO_NOME}</div>
        <h2 data-convite-texto>{terminou ? 'Você terminou a 1ª etapa.' : 'A 1ª etapa é grátis.'} {outras} e todas as trilhas do acervo: {ano} por ano.</h2>
        <p>O Mentor segue com você até o fim do livro, com os comentários, os insights e os desafios de cada etapa.</p>
        <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
          {isNativeApp() ? (
            onAssinar && <button type="button" className="tv-botao" onClick={onAssinar} data-convite-assinar>Assinar o Pro · {ano}/ano</button>
          ) : (
            <div data-convite-lojas>
              <div className="tv-mono" style={{ color: 'var(--tv-caminho)', marginBottom: 6 }}>Assine pelo app</div>
              <p style={{ fontSize: 13.5, marginBottom: 12 }} data-linha-so-lojas>{LINHA_SO_LOJAS} {ano} por ano.</p>
              <LojasDoApp de="convite" />
            </div>
          )}
          <button type="button" className="tv-botao tv-sec" onClick={onFechar} data-convite-fechar>Agora não</button>
        </div>
      </div>
    </div>
  );
}

/** Corpo curto (desafio, marco): o markdown sem a margem grande. */
function MdTvCurto({ md }: { md: string }) {
  return <div className="tv-md" style={{ marginTop: 6 }}>{blocos(md).map((b, k) => <p key={k} style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink-2)', margin: '0 0 6px' }}>{inline(b.linhas.join(' '), 'c' + k)}</p>)}</div>;
}

/** A folha de um insight. Também abre um desafio (vindo da Home: "Ontem você levou"),
 *  com o Feito / Não feito no lugar. */
function FolhaInsight({ item, falhou, onFechar, onAvaliar, onCaderno, desafio }: { item: TrilhaItem; falhou: boolean; onFechar: () => void; onAvaliar: (v: TrilhaAvaliacao | null) => void; onCaderno: (contexto: ContextoDaNota | null) => void; desafio?: React.ReactNode }) {
  const { resto, pergunta } = separarPergunta(item.corpo);
  return (
    <div className="tv-folha" role="dialog" aria-modal="true" aria-label={item.titulo || 'Insight'} data-folha={item.id}>
      <div className="tv-folha-topo">
        <button type="button" className="tv-botao-redondo" onClick={onFechar} aria-label="Fechar">{Ic.fechar}</button>
        <div className="tv-mono" style={{ color: 'var(--tv-caminho)' }}>{desafio ? 'Desafio' : 'Insight'} · p. {item.pagina_gatilho}</div>
      </div>
      <div className="tv-folha-corpo">
        <h2>{item.titulo}</h2>
        {item.extra?.imagem && <FiguraParada imagem={item.extra.imagem} legenda folha />}
        <MdTv md={resto} />
        {desafio && <div style={{ marginTop: 14 }} data-folha-desafio>{desafio}</div>}
        {pergunta && (
          <div className="tv-pergunta">
            <div className="tv-mono" style={{ color: 'var(--tv-caminho)' }}>Para o caderno</div>
            <p style={{ margin: '6px 0 12px', fontFamily: 'var(--font-serif)', fontSize: 20, lineHeight: 1.3 }}>{pergunta.replace(/\*\*|__/g, '')}</p>
            <button type="button" className="tv-botao" onClick={() => onCaderno(contextoDoInsight(item.titulo, item.pagina_gatilho, pergunta))} data-responder>Responder no caderno</button>
          </div>
        )}
        {!desafio && (
          <div className="tv-chips" role="group" aria-label="Como este insight chegou para você?" style={{ marginTop: 18 }}>
            {OPCOES.map((o) => <button key={o.v} type="button" className="tv-chip" aria-pressed={item.avaliacao === o.v} data-avaliacao={o.v} onClick={() => onAvaliar(item.avaliacao === o.v ? null : o.v)}>{o.rotulo}</button>)}
          </div>
        )}
        {falhou && <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>Não salvou. Tente de novo.</p>}
      </div>
    </div>
  );
}
